/* eslint-disable react-hooks/rules-of-hooks */
import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { Formik, FormikProps, FormikConfig, FormikHelpers } from 'formik';
import { Form } from 'antd';

import FormField from 'components/FormField';
import HintInput from 'components/HintInput';
import { useQaContext } from 'views/Aide/Qa/Context';
import ScenarioPackPicker from './ScenarioPackPicker';
import { StoreIdsSelect } from 'views/Aide/components/StoreIdSelect';
import { useNonPlatformQaEnable } from 'hooks/business/useOrgConfig';

export interface StoreGroupFormValues
  extends Omit<Store.StoreGroup, 'id' | 'orgId' | 'watchLimitEnable'> {
  id?: Store.StoreGroup['id'];
  scenePackValue: Store.ScenarioPackValue;
}

type FV = StoreGroupFormValues;

export type StoreGroupFormHelpers = FormikHelpers<FV>;

type FormRenderChildren<FV> = (
  nodes: {
    storeIdsField: ReactNode;
    nameField: ReactNode;
    scenePackValueField: ReactNode;
    form: ReactNode;
  },
  formProps: FormikProps<FV>,
) => ReactNode;

export type FormMode = 'force' | 'normal';

export interface StoreGroupFormProps extends Omit<FormikConfig<FV>, 'initialValues'> {
  mode: FormMode;
  initialValues?: FV;
  children?: ReactNode | FormRenderChildren<FV>;
  storeList: Store.Store[];
  storeGroupList: Store.StoreGroup[];
}

export const createStoreGroupFormInitialValues = (
  partialValues: Pick<FV, 'region' | 'externalType'> & Partial<FV>,
): FV => {
  return {
    name: '',
    configUserId: [],
    storeIds: [],
    scenePackValue: {
      scenePacks: [],
      mainVersionGroupId: 0,
    },
    status: 'VALID',
    ...partialValues,
  };
};

const nameLimit = 32;

function StoreGroupForm(props: StoreGroupFormProps) {
  const {
    mode,
    initialValues: initialValuesProp,
    children,
    storeList,
    storeGroupList,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const { platform, region } = useQaContext();

  const nonPlatformQaEnable = useNonPlatformQaEnable();

  const initialValues = useMemo(() => {
    if (initialValuesProp) {
      return initialValuesProp;
    }

    return createStoreGroupFormInitialValues({
      region,
      externalType: nonPlatformQaEnable ? undefined : platform,
    });
  }, [initialValuesProp, region, nonPlatformQaEnable, platform]);

  const isRenderProps = typeof children === 'function';

  const { unusedStoreList, storeMap, storeGroupNameMap } = useMemo(() => {
    const { usedStoreIdMap, storeGroupNameMap } = storeGroupList.reduce<{
      usedStoreIdMap: Record<number, boolean>;
      storeGroupNameMap: Record<string, boolean>;
    }>(
      (res, storeGroup) => {
        const { usedStoreIdMap, storeGroupNameMap } = res;

        const { id, storeIds, name } = storeGroup;

        if (id === initialValues.id) {
          return res;
        }

        storeIds.forEach((storeId) => {
          usedStoreIdMap[storeId] = true;
        });

        storeGroupNameMap[name] = true;

        return res;
      },
      {
        usedStoreIdMap: {},
        storeGroupNameMap: {},
      },
    );

    const { unusedStoreList, storeMap } = storeList.reduce<{
      unusedStoreList: Store.Store[];
      storeMap: Record<Store.Store['id'], Store.Store>;
    }>(
      (res, store) => {
        const { unusedStoreList, storeMap } = res;

        const { id } = store;

        if (usedStoreIdMap[id] === undefined) {
          unusedStoreList.push(store);
        }

        storeMap[id] = store;

        return res;
      },
      {
        unusedStoreList: [],
        storeMap: {},
      },
    );

    return {
      unusedStoreList,
      storeMap,
      usedStoreIdMap,
      storeGroupNameMap,
    };
  }, [storeGroupList, storeList, initialValues.id]);

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof typeof values, any>> = {};

      const { name, storeIds, scenePackValue: { mainVersionGroupId } = {} } = values;

      if (!name || !name.trim()) {
        errors.name = t('storeGroup.请输入店铺配置组名称');
      } else if (name.length > nameLimit) {
        errors.name = t('storeGroup.店铺配置组名称不得超出x个字', { x: nameLimit });
      } else if (storeGroupNameMap[name]) {
        errors.name = t('storeGroup.已存在该店铺配置组名称');
      }

      if (!storeIds.length || storeIds.length === 0) {
        errors.storeIds = t('storeGroup.请选择店铺');
      }

      if (!mainVersionGroupId) {
        errors.scenePackValue = '';
      }

      return errors;
    },
    [storeGroupNameMap, t],
  );

  return (
    <Formik enableReinitialize initialValues={initialValues} validate={validate} {...otherProps}>
      {(props) => {
        const {
          values: { id, storeIds },
        } = props;

        const currentDomainList = useMemo(() => {
          return storeIds.reduce<string[]>((res, storeId) => {
            const { [storeId]: { domain = undefined } = {} } = storeMap;

            if (domain && !res.includes(domain)) {
              res.push(domain);
            }

            return res;
          }, []);
        }, [storeIds]);

        const storeIdsField = (
          <FormField<FV['storeIds']> name="storeIds" required label={t('storeGroup.适用店铺')}>
            {({ field: { value, onChange } }) => {
              return (
                <StoreIdsSelect
                  value={value}
                  onChange={onChange}
                  storeMap={storeMap}
                  storeDataSource={unusedStoreList}
                />
              );
            }}
          </FormField>
        );

        const nameField = (
          <FormField<FV['name']> name="name" required label={t('storeGroup.店铺配置组名称')}>
            {({ field }) => {
              return (
                <HintInput
                  placeholder={t('storeGroup.请输入店铺配置组名称')}
                  maxLength={nameLimit}
                  limit={nameLimit}
                  {...field}
                />
              );
            }}
          </FormField>
        );

        const scenePackValueField = (
          <ScenarioPackPicker storeGroupId={id} domainList={currentDomainList} />
        );

        const form = (
          <Form>
            {storeIdsField}
            {nameField}
            {scenePackValueField}
          </Form>
        );

        return isRenderProps
          ? (children as FormRenderChildren<FV>)(
              {
                storeIdsField,
                nameField,
                scenePackValueField,
                form,
              },
              props,
            )
          : form;
      }}
    </Formik>
  );
}

export default StoreGroupForm;
